import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TAX_DISCLAIMER,
  computeInsurance,
  computeTax,
  type TaxBracket,
} from '@goyone/shared';
import { ErrorState, Notice, PageHead } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { money, num } from '../../lib/format';

interface Rules {
  insuranceEmployeeRate: number;
  insuranceEmployerRate: number;
  insuranceWageMin: number;
  insuranceWageMax: number;
  taxPersonalExemption: number;
  taxBrackets: TaxBracket[];
  taxRulesNote: string;
  currencyLabel: string;
}

/**
 * قواعدُ التأمينات وضريبة الدخل.
 *
 * أرقامٌ يُدخلها المحاسبُ لا تُكتب في الشيفرة: القوانينُ تتغيّر بقرارٍ في
 * الجريدة الرسمية، ونظامٌ يحبس نسبةً في شيفرته يجعل تعديلَ قانونٍ إصدارَ
 * برنامج.
 *
 * والحاسبةُ في أسفل الشاشة ليست زينة: من يعدّل شريحةً يريد أن يرى أثرَها
 * على راتبٍ يعرفه قبل أن يحفظ — لا في قسائم الشهر بعد أن تُصرف.
 */
export function PayrollRules() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<{ settings: Rules }>('/settings'),
  });

  const [rules, setRules] = useState<Rules | null>(null);
  const [preview, setPreview] = useState('12000');

  useEffect(() => {
    if (profile.data && !rules) setRules(profile.data.settings);
  }, [profile.data, rules]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/preferences', {
        method: 'PATCH',
        body: {
            insuranceEmployeeRate: rules!.insuranceEmployeeRate,
            insuranceEmployerRate: rules!.insuranceEmployerRate,
            insuranceWageMin: rules!.insuranceWageMin,
            insuranceWageMax: rules!.insuranceWageMax,
            taxPersonalExemption: rules!.taxPersonalExemption,
            taxBrackets: rules!.taxBrackets,
          taxRulesNote: rules!.taxRulesNote,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast('حُفظت القواعد');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  if (profile.isError) {
    return (
      <div className="page">
        <PageHead title="التأمينات والضريبة" />
        <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
      </div>
    );
  }

  if (!rules) {
    return (
      <div className="page">
        <PageHead title="التأمينات والضريبة" />
        <div className="skeleton" style={{ height: 240 }} />
      </div>
    );
  }

  const set = (patch: Partial<Rules>) => setRules({ ...rules, ...patch });

  const monthly = Number(preview) || 0;
  const ins = computeInsurance(null, monthly, {
    employeeRate: rules.insuranceEmployeeRate,
    employerRate: rules.insuranceEmployerRate,
    wageMin: rules.insuranceWageMin,
    wageMax: rules.insuranceWageMax,
  });
  const tax = computeTax(Math.max(0, monthly - ins.employeeShare), {
    brackets: rules.taxBrackets,
    personalExemption: rules.taxPersonalExemption,
    note: rules.taxRulesNote,
  });
  const currency = rules.currencyLabel || 'ج.م';

  return (
    <div className="page">
      <PageHead
        title="التأمينات والضريبة"
        subtitle="النِّسب والشرائح التي يحسب بها المرتّب"
        actions={
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        }
      />

      {/*
        التنبيهُ لا يُصغَّر ولا يُخفى: من يقرأ رقمًا في شاشةٍ يفترض أنّه
        صحيحٌ قانونًا، وهذا النظامُ يحسب بما أُدخل فيه لا بما في القانون.
      */}
      <Notice title="ما يُحسب هنا يُراجَع" icon="alert" kind="warning">
        {TAX_DISCLAIMER}
      </Notice>

      <div className="card">
        <h2 className="card-title">التأمينات الاجتماعية</h2>
        <p className="tiny mute">
          الأجرُ التأمينيُّ غيرُ الراتب الأساسيّ: له حدٌّ أدنى وأعلى، ويُضبط
          لكلّ موظّفٍ في ملفّه. وحصّةُ صاحب العمل تُسجَّل تكلفةً ولا تُخصم من
          الموظّف.
        </p>

        <div className="form-grid mt-4">
          <Num
            id="ins-emp"
            label="حصّة الموظّف ٪"
            value={rules.insuranceEmployeeRate}
            onChange={(v) => set({ insuranceEmployeeRate: v })}
          />
          <Num
            id="ins-org"
            label="حصّة صاحب العمل ٪"
            value={rules.insuranceEmployerRate}
            onChange={(v) => set({ insuranceEmployerRate: v })}
          />
          <Num
            id="ins-min"
            label="أدنى أجرٍ تأمينيّ"
            value={rules.insuranceWageMin}
            onChange={(v) => set({ insuranceWageMin: v })}
          />
          <Num
            id="ins-max"
            label="أقصى أجرٍ تأمينيّ"
            value={rules.insuranceWageMax}
            onChange={(v) => set({ insuranceWageMax: v })}
          />
        </div>
      </div>

      <div className="card">
        <h2 className="card-title">ضريبة الدخل</h2>
        <p className="tiny mute">
          الشرائحُ سنويّةٌ وتصاعديّة: كلُّ شريحةٍ تُطبَّق على ما بينها وبين
          سابقتها لا على الوعاء كلِّه. والإعفاءُ الشخصيُّ يُطرح من الوعاء لا
          من الضريبة.
        </p>

        <div className="form-grid mt-4">
          <Num
            id="tax-exempt"
            label="الإعفاء الشخصي السنوي"
            value={rules.taxPersonalExemption}
            onChange={(v) => set({ taxPersonalExemption: v })}
          />
          <div className="field span-2">
            <label className="label" htmlFor="tax-note">
              أيُّ قانونٍ أدخلتَ ومتى
            </label>
            <input
              id="tax-note"
              className="input"
              value={rules.taxRulesNote}
              onChange={(e) => set({ taxRulesNote: e.target.value })}
              placeholder="شرائح قانون كذا — راجعها المحاسب في …"
            />
            <span className="tiny mute">
              تُكتب على قسيمة كلّ موظّفٍ ليعرف من يقرؤها بأيّ قواعدَ حُسبت.
            </span>
          </div>
        </div>

        <h3 className="card-title mt-4">الشرائح</h3>
        <div className="rc-fields">
          {rules.taxBrackets.map((b, i) => (
            <div className="rc-field-row" key={i}>
              <span className="tiny mute" style={{ minWidth: 60 }}>
                {i === 0
                  ? 'حتى'
                  : b.upTo === null
                    ? 'وما فوق'
                    : `من ${num(rules.taxBrackets[i - 1]!.upTo ?? 0)} حتى`}
              </span>

              <input
                className="input num"
                type="number"
                min={0}
                value={b.upTo ?? ''}
                placeholder="بلا حدّ"
                onChange={(e) =>
                  set({
                    taxBrackets: rules.taxBrackets.map((x, j) =>
                      i === j
                        ? { ...x, upTo: e.target.value ? Number(e.target.value) : null }
                        : x,
                    ),
                  })
                }
              />

              <input
                className="input num"
                type="number"
                step="0.5"
                min={0}
                max={100}
                value={b.rate}
                onChange={(e) =>
                  set({
                    taxBrackets: rules.taxBrackets.map((x, j) =>
                      i === j ? { ...x, rate: Number(e.target.value) || 0 } : x,
                    ),
                  })
                }
              />
              <span className="tiny mute">٪</span>

              <button
                type="button"
                className="btn btn-icon btn-ghost btn-sm"
                aria-label="حذف الشريحة"
                onClick={() =>
                  set({ taxBrackets: rules.taxBrackets.filter((_, j) => j !== i) })
                }
              >
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn mt-2"
          onClick={() =>
            set({ taxBrackets: [...rules.taxBrackets, { upTo: null, rate: 0 }] })
          }
        >
          <Icon name="plus" size={14} />
          شريحة جديدة
        </button>
      </div>

      <div className="card">
        <h2 className="card-title">جرّبها على راتب</h2>
        <p className="tiny mute">
          قبل الحفظ — لا في قسائم الشهر بعد أن تُصرف.
        </p>

        <div className="field mt-2" style={{ maxWidth: 240 }}>
          <label className="label" htmlFor="pv">
            راتبٌ شهريٌّ إجماليّ
          </label>
          <input
            id="pv"
            className="input num"
            type="number"
            min={0}
            value={preview}
            onChange={(e) => setPreview(e.target.value)}
          />
        </div>

        <dl className="rc-offer-facts mt-4">
          <span>
            الأجر التأمينيّ: <b className="num">{money(ins.wage, currency)}</b>
            {ins.clamped === 'MIN' && ' (بالحدّ الأدنى)'}
            {ins.clamped === 'MAX' && ' (بالحدّ الأقصى)'}
          </span>
          <span>
            حصّة الموظّف:{' '}
            <b className="num">{money(ins.employeeShare, currency)}</b>
          </span>
          <span>
            حصّة الشركة:{' '}
            <b className="num">{money(ins.employerShare, currency)}</b>
          </span>
          <span>
            الوعاء السنويّ: <b className="num">{money(tax.annualBase, currency)}</b>
          </span>
          <span>
            ضريبة الشهر: <b className="num">{money(tax.monthlyTax, currency)}</b>
          </span>
          <span>
            الصافي:{' '}
            <b className="num">
              {money(monthly - ins.employeeShare - tax.monthlyTax, currency)}
            </b>
          </span>
        </dl>

        {tax.slices.length > 0 && (
          <div className="table-wrap mt-4">
            <table className="table">
              <thead>
                <tr>
                  <th>من</th>
                  <th>إلى</th>
                  <th>النسبة</th>
                  <th>ضريبة الشريحة سنويًّا</th>
                </tr>
              </thead>
              <tbody>
                {tax.slices.map((s, i) => (
                  <tr key={i}>
                    <td className="num">{num(s.from)}</td>
                    <td className="num">{s.to === null ? 'وما فوق' : num(s.to)}</td>
                    <td className="num">{s.rate}٪</td>
                    <td className="num">{money(s.tax, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Num({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input num"
        type="number"
        step="0.25"
        min={0}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </div>
  );
}
